import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Search, BookOpen, ExternalLink, Bookmark, MessageSquare, Filter } from 'lucide-react';
import axios from 'axios';
import { API_BASE } from '@/lib/api';
import { useNavigate } from 'react-router-dom';

const AGENCY_COLORS = {
  FDA: 'bg-blue-500/15 text-blue-400 border-blue-500/20',
  EMA: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
  ICH: 'bg-violet-500/15 text-violet-400 border-violet-500/20',
  PMDA: 'bg-orange-500/15 text-orange-400 border-orange-500/20',
  NMPA: 'bg-red-500/15 text-red-400 border-red-500/20',
};

export default function GuidancesPage() {
  const [guidances, setGuidances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [agencyFilter, setAgencyFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [bookmarks, setBookmarks] = useState(() => JSON.parse(localStorage.getItem('pharmax_bookmarks') || '[]'));
  const navigate = useNavigate();

  useEffect(() => { loadGuidances(); }, [agencyFilter]);

  const loadGuidances = async () => {
    setLoading(true);
    try {
      const params = {};
      if (agencyFilter !== 'all') params.agency = agencyFilter;
      const res = await axios.get(`${API_BASE}/guidances`, { params });
      setGuidances(res.data.guidances || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSearch = useCallback(async () => {
    if (!search.trim()) { loadGuidances(); return; }
    setLoading(true);
    try {
      const params = { search: search.trim() };
      if (agencyFilter !== 'all') params.agency = agencyFilter;
      const res = await axios.get(`${API_BASE}/guidances`, { params });
      setGuidances(res.data.guidances || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [search, agencyFilter]);

  useEffect(() => {
    const timer = setTimeout(handleSearch, 300);
    return () => clearTimeout(timer);
  }, [search, handleSearch]);

  const toggleBookmark = (id) => {
    const next = bookmarks.includes(id) ? bookmarks.filter(b => b !== id) : [...bookmarks, id];
    setBookmarks(next);
    localStorage.setItem('pharmax_bookmarks', JSON.stringify(next));
  };

  const filtered = guidances;
  const agencies = ['all', 'FDA', 'EMA', 'ICH', 'PMDA', 'NMPA'];

  return (
    <div className="p-6 sm:p-8 max-w-[1200px] mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>Regulatory Guidances</h1>
        <p className="text-sm text-muted-foreground mt-1">Search FDA, EMA, ICH, PMDA, and NMPA guidances</p>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search guidances... (e.g., 'renal impairment dose adjustment', 'PopPK covariate')"
          className="pl-9 h-11 rounded-xl" data-testid="guidance-search" />
      </div>

      {/* Agency filter */}
      <div className="flex gap-1.5 mb-6 flex-wrap">
        {agencies.map(a => (
          <Button key={a} size="sm" variant={agencyFilter === a ? 'default' : 'outline'}
            onClick={() => setAgencyFilter(a)}
            className={`rounded-full h-8 text-xs ${a !== 'all' && agencyFilter === a ? AGENCY_COLORS[a] || '' : ''}`}>
            {a === 'all' ? `All (${guidances.length})` : a}
          </Button>
        ))}
      </div>

      {/* Results */}
      {loading ? (
        <div className="space-y-3">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <BookOpen className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No guidances found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((g, i) => (
            <motion.div key={g.id || i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i * 0.03, 0.3) }}>
              <Card className="card-hover cursor-pointer" onClick={() => setSelected(g)}>
                <CardContent className="py-4 px-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${AGENCY_COLORS[g.agency] || 'bg-muted text-muted-foreground'}`}>
                          {g.agency}
                        </span>
                        {g.year && <span className="text-[10px] font-mono text-muted-foreground">{g.year}</span>}
                        <span className="text-[10px] font-mono text-muted-foreground/50 capitalize">{g.category?.replace(/_/g, ' ')}</span>
                      </div>
                      <h3 className="text-sm font-semibold leading-snug" style={{ fontFamily: 'Space Grotesk' }}>{g.title}</h3>
                      <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">{g.content}</p>
                      {g.topics?.length > 0 && (
                        <div className="flex gap-1 mt-2">
                          {g.topics.slice(0, 4).map(t => (
                            <span key={t} className="text-[9px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{t}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button variant="ghost" size="icon" className="h-8 w-8"
                        onClick={(e) => { e.stopPropagation(); toggleBookmark(g.id); }}>
                        <Bookmark className={`w-4 h-4 ${bookmarks.includes(g.id) ? 'fill-primary text-primary' : 'text-muted-foreground'}`} />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${AGENCY_COLORS[selected?.agency] || ''}`}>
                {selected?.agency}
              </span>
              <span className="text-xs font-mono text-muted-foreground">{selected?.year}</span>
            </div>
            <DialogTitle style={{ fontFamily: 'Space Grotesk' }}>{selected?.title}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[50vh] mt-4">
            <div className="text-sm leading-7 text-foreground/90 whitespace-pre-wrap pr-4">
              {selected?.content}
            </div>
          </ScrollArea>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" size="sm" className="rounded-full text-xs"
              onClick={() => { toggleBookmark(selected?.id); }}>
              <Bookmark className={`w-3.5 h-3.5 mr-1 ${bookmarks.includes(selected?.id) ? 'fill-primary text-primary' : ''}`} />
              {bookmarks.includes(selected?.id) ? 'Bookmarked' : 'Bookmark'}
            </Button>
            <Button size="sm" className="rounded-full text-xs" onClick={() => {
              setSelected(null);
              navigate('/app/projects');
              // In a real implementation, this would open a project chat with pre-filled question
            }}>
              <MessageSquare className="w-3.5 h-3.5 mr-1" /> Ask AI About This
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
